import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/Key";
import { setModal } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";
import apiClient from "../../services/apiClient";
import toast from "react-hot-toast";

const ApiKeys = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);

  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // API response data
  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    total_pages: 0,
  });

  useEffect(() => {
    fetchApiKeys();
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (modal?.isUpdatedIn === "API_KEYS") {
      fetchApiKeys();
    }
  }, [modal]);

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const transformPermissions = (permissionsObj) => {
    if (!permissionsObj) return [];
    return Object.keys(permissionsObj)
      .filter((key) => permissionsObj[key] === true)
      .map((key) => key.toUpperCase());
  };

  const transformApiData = (data) => {
    return data.map((item) => ({
      id: item._id,
      name: item.name,
      maskedKey: item.masked_key,
      fullKey: item.masked_key,
      type: item.type.charAt(0).toUpperCase() + item.type.slice(1),
      permissions: transformPermissions(item.permissions),
      rateLimit: `${item.rate_limit?.requests_per_minute || 0}/minute`,
      status: item.status.charAt(0).toUpperCase() + item.status.slice(1),
      createdAt: formatDate(item.created_at),
      lastUsed: formatDate(item.usage_stats?.last_used),
      usageCount: item.usage_stats?.total_calls || 0,
    }));
  };

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/api-keys", {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      });

      if (response.status && response.data) {
        const transformedData = transformApiData(response.data);
        setApiKeys(transformedData);
        setPaginationInfo({
          total_count: response.pagination?.total_items || 0,
          total_pages: response.pagination?.total_pages || 0,
        });
      } else {
        console.error("Failed to fetch API keys:", response.message);
        setApiKeys([]);
      }
    } catch (error) {
      console.error("Error fetching API keys:", error);
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = () => {
    dispatch(setModal({ type: "CREATE_API_KEY" }));
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key.maskedKey);
    alert(`API key copied to clipboard!`);
  };

  const handleViewDetails = async (key) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api-keys/${key.id}`);
      if (response.status && response.data) {
        dispatch(setModal({ type: "VIEW_API_KEY_DETAILS", data: response.data }));
      } else {
        toast.error("Failed to fetch API key details");
      }
    } catch (error) {
      console.error("Error fetching API key details:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch API key details");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateKey = async (key) => {
    if (window.confirm(`Regenerate API key "${key.name}"? The old key will be invalidated.`)) {
      setLoading(true);
      try {
        const response = await apiClient.post(`/api-keys/${key.id}/renew`);
        if (response.status && response.data) {
          toast.success(response.message || "Key rotated successfully");
          fetchApiKeys();
        } else {
          toast.error(response.message || "Failed to regenerate API key");
        }
      } catch (error) {
        console.error("Error regenerating API key:", error);
        toast.error(error?.response?.data?.message || "Failed to regenerate API key");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteKey = async (key) => {
    if (window.confirm(`Delete API key "${key.name}"? This action cannot be undone.`)) {
      setLoading(true);
      try {
        const response = await apiClient.delete(`/api-keys/${key.id}`);
        if (response.status) {
          setApiKeys(apiKeys.filter((k) => k.id !== key.id));
          toast.success(response.message || "API key deleted successfully");
        } else {
          toast.error(response.message || "Failed to delete API key");
        }
      } catch (error) {
        console.error("Error deleting API key:", error);
        toast.error(error?.response?.data?.message || "Failed to delete API key");
      } finally {
        setLoading(false);
      }
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      Public: { bg: "#E3F2FD", color: "#1565C0" },
      Private: { bg: "#F3E5F5", color: "#7B1FA2" },
      Admin: { bg: "#FFEBEE", color: "#C62828" },
    };
    return colors[type] || { bg: "#F5F5F5", color: "#666" };
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Key Name",
        size: 180,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundColor: "#F3F4F6",
                border: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6B7280",
              }}
            >
              <KeyIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600} color="#1F2937" sx={{ fontSize: "0.875rem" }}>
                {row.original.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                {row.original.createdAt}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: "maskedKey",
        header: "API Key",
        size: 250,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.813rem",
                color: "#6B7280",
              }}
            >
              {row.original.maskedKey}
            </Typography>
            <Tooltip title="Copy" arrow placement="top">
              <IconButton
                size="small"
                onClick={() => handleCopyKey(row.original)}
                sx={{
                  color: "#9CA3AF",
                  "&:hover": {
                    color: "#3B82F6",
                    backgroundColor: "#EFF6FF"
                  }
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 100,
        Cell: ({ cell }) => {
          const colors = getTypeColor(cell.getValue());
          return (
            <Chip
              label={cell.getValue()}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 500,
                fontSize: "0.75rem",
                height: "24px",
                borderRadius: "6px",
              }}
            />
          );
        },
      },
      // {
      //   accessorKey: "permissions",
      //   header: "Permissions",
      //   size: 200,
      //   Cell: ({ row }) => (
      //     <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      //       {row.original.permissions.map((permission, idx) => (
      //         <Chip
      //           key={idx}
      //           label={permission}
      //           size="small"
      //           sx={{
      //             fontSize: 10,
      //             height: 20,
      //             backgroundColor: "#E8F5E9",
      //             color: "#2E7D32",
      //             fontWeight: 500,
      //           }}
      //         />
      //       ))}
      //     </Box>
      //   ),
      // },
      {
        accessorKey: "rateLimit",
        header: "Rate Limit",
        size: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="#6B7280" sx={{ fontSize: "0.875rem" }}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={cell.getValue() === "Active" ? "success" : "default"}
            sx={{
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
              borderRadius: "6px",
            }}
          />
        ),
      },
      {
        accessorKey: "usageCount",
        header: "Usage",
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="#3B82F6" sx={{ fontSize: "0.875rem" }}>
            {cell.getValue().toLocaleString()}
          </Typography>
        ),
      },
    ],
    []
  );

  const totalKeys = apiKeys.length;
  const activeKeys = apiKeys.filter((k) => k.status === "Active").length;
  const publicKeys = apiKeys.filter((k) => k.type === "Public").length;
  const totalUsage = apiKeys.reduce((sum, k) => sum + k.usageCount, 0);

  return (
    <>
      {/* Header */}
      <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
        <div>
          <h3>API Keys</h3>
          <p>Generate and manage API keys with custom permissions and rate limits</p>
        </div>
        <CustomButton
          icon={<AddIcon />}
          iconPosition="left"
          onClick={handleCreateKey}
          text="Create New Key"
        />
      </div>

      <Box sx={{ padding: "24px 40px" }}>
        {/* Stats Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 2,
            mb: 3
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderRadius: 1.5,
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#3B82F6",
                boxShadow: "0 2px 8px rgba(59, 130, 246, 0.08)"
              }
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: "0.813rem" }}>
              Total Keys
            </Typography>
            <Typography variant="h5" fontWeight={600} color="#1F2937">
              {totalKeys}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 1.5,
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#10B981",
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.08)"
              }
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: "0.813rem" }}>
              Active Keys
            </Typography>
            <Typography variant="h5" fontWeight={600} color="#10B981">
              {activeKeys}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 1.5,
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#8B5CF6",
                boxShadow: "0 2px 8px rgba(139, 92, 246, 0.08)"
              }
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: "0.813rem" }}>
              Public / Private
            </Typography>
            <Typography variant="h5" fontWeight={600} color="#8B5CF6">
              {publicKeys} / {totalKeys - publicKeys}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 1.5,
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#F59E0B",
                boxShadow: "0 2px 8px rgba(245, 158, 11, 0.08)"
              }
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: "0.813rem" }}>
              Total Calls
            </Typography>
            <Typography variant="h5" fontWeight={600} color="#F59E0B">
              {totalUsage.toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {/* Table */}
        <MaterialReactTable
          columns={columns}
          data={apiKeys}
          manualPagination
          rowCount={paginationInfo.total_count}
          onPaginationChange={setPagination}
          state={{
            isLoading: loading,
            pagination: pagination,
          }}
          enableRowActions
          positionActionsColumn="last"
          renderRowActions={({ row }) => (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="View Details" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => handleViewDetails(row.original)}
                  sx={{
                    color: "#6B7280",
                    "&:hover": {
                      color: "#3B82F6",
                      backgroundColor: "#EFF6FF"
                    }
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Regenerate Key" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => handleRegenerateKey(row.original)}
                  sx={{
                    color: "#6B7280",
                    "&:hover": {
                      color: "#F59E0B",
                      backgroundColor: "#FEF3C7"
                    }
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteKey(row.original)}
                  sx={{
                    color: "#6B7280",
                    "&:hover": {
                      color: "#EF4444",
                      backgroundColor: "#FEE2E2"
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          enableColumnFilters
          enableGlobalFilter
          enableSorting
          enablePagination
          muiTablePaginationProps={{
            showFirstButton: true,
            showLastButton: true,
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 1.5,
              border: "1px solid #E5E7EB",
              backgroundColor: "#fff",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#F9FAFB",
              fontWeight: 600,
              color: "#374151",
              fontSize: "0.813rem",
              borderBottom: "1px solid #E5E7EB",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              borderBottom: "1px solid #F3F4F6",
              fontSize: "0.875rem",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              "&:hover": {
                backgroundColor: "#F9FAFB",
              },
            },
          }}
        />
      </Box>
    </>
  );
};

export default ApiKeys;
